import { gql, useMutation } from '@apollo/client'
import CancelIcon from '@mui/icons-material/Cancel'
import DeleteIcon from '@mui/icons-material/Delete'
import { Button, Divider, Stack, Typography, useTheme } from '@mui/material'
import Modal from 'components/Modal'
import { VariantType, useSnackbar } from 'notistack'
import React, { useContext, useState } from 'react'
import { Stage } from 'utils/__generated__/graphql'

import { STAGE_FRAGMENT } from '../../constants'
import { DATAROOM_FRAGMENT } from '../../../constants'
import DataroomContext, { DataroomContextType } from '../../context'

const DELETE_STAGE = gql`
  ${DATAROOM_FRAGMENT}
  ${STAGE_FRAGMENT}
  mutation DELETE_STAGE($dataroomId: ID!, $stageId: ID!) {
    deleteStage(dataroomId: $dataroomId, stageId: $stageId) {
      ...DataroomFragment
      stages {
        ...StageFragment
      }
    }
  }
`

function Delete({ stage }: { stage: Stage }) {
  const { dataroom } = useContext<DataroomContextType>(DataroomContext)
  const [open, setOpen] = useState(false)

  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  const handleNotification = (variant: VariantType, message: string) => () => {
    enqueueSnackbar(message, { variant, autoHideDuration: 3000 })
  }

  const onSuccess = handleNotification('success', 'Delete Success!')
  const onError = handleNotification('error', 'Something went wrong!')

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [deleteStage] = useMutation(DELETE_STAGE)

  const handleDelete = () => {
    deleteStage({
      variables: { dataroomId: dataroom.id, stageId: stage.id },
      onCompleted: () => {
        handleClose()
        onSuccess()
      },
      onError,
    })
  }

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={420}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Delete - {stage.name}
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        <Stack spacing={1}>
          <Typography
            sx={{
              ...theme.typography.body1,
              color: theme.palette.text.secondary,
            }}
          >
            Are you sure you want to delete this stage? This action cannot be
            undone.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: 'flex-end' }}
          >
            <Button
              color="inherit"
              disableElevation
              onClick={handleClose}
              variant="contained"
              size="small"
            >
              Cancel
            </Button>
            <Button
              color="error"
              disableElevation
              onClick={handleDelete}
              variant="contained"
              size="small"
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Modal>
      <Button
        size="small"
        color="error"
        variant="outlined"
        startIcon={<DeleteIcon />}
        onClick={handleOpen}
      >
        Delete
      </Button>
    </React.Fragment>
  )
}

export default Delete
